/*
 * KI-Label (AI Act Art. 50) – dezentes, adaptives „AI“-Label auf KI-Bildern.
 *
 * Erwartet window.KI_LABEL = {
 *   files:   { "dateiname.jpg": "generated" | "modified", ... },  // aus Metaobjekt „KI-Kennzeichnung“
 *   alpha:   0.06,        // Wunsch-Deckkraft der Pille (wird nur erhöht, wenn der Zielkontrast sonst unerreichbar ist)
 *   contrast: 3,          // Zielkontrast Schrift/Untergrund, wird pro Bild genau angesteuert (WCAG AA wäre 4,5 für kleine Schrift)
 *   href:    "/pages/ki-transparenz",
 *   text:    { generated: "KI-generiertes Bild", modified: "Mit KI bearbeitetes Bild" }
 * }
 */
(() => {
  const cfg = Object.assign({
    files: {}, alpha: 0.06, contrast: 3, href: '/pages/ki-transparenz',
    text: { generated: 'KI-generiertes Bild', modified: 'Mit KI bearbeitetes Bild' },
    short: { generated: 'KI-generiert', modified: 'KI-bearbeitet' },
  }, window.KI_LABEL || {});
  const files = cfg.files;

  // --- Farbe ---------------------------------------------------------------
  const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const lum = ([r, g, b]) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  const contrast = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p); return (x + 0.05) / (y + 0.05); };
  const mix = (a, b, t) => a.map((v, i) => Math.round(v * (1 - t) + b[i] * t));
  const rgb = (c, a = 1) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

  // Farbfelder des Bildbereichs unter dem Label (berücksichtigt object-fit: cover)
  function sample(img, box) {
    const nw = img.naturalWidth, nh = img.naturalHeight;
    const r = img.getBoundingClientRect();
    if (!nw || !r.width) return null;
    const fit = getComputedStyle(img).objectFit;
    const s = fit === 'cover' ? Math.max(r.width / nw, r.height / nh)
            : fit === 'contain' ? Math.min(r.width / nw, r.height / nh)
            : r.width / nw;
    const ox = (r.width - nw * s) / 2, oy = (r.height - nh * s) / 2;
    const c = document.createElement('canvas');
    c.width = 8; c.height = 4;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    try {
      ctx.drawImage(img, (box.x - ox) / s, (box.y - oy) / s, box.w / s, box.h / s, 0, 0, 8, 4);
      const d = ctx.getImageData(0, 0, 8, 4).data;
      const cells = [];
      for (let i = 0; i < d.length; i += 4) cells.push([d[i], d[i + 1], d[i + 2]]);
      return cells; // 32 Messfelder unter dem Label
    } catch (e) {
      return null; // Bild cross-origin ohne CORS → neutrale Rückfallfarbe
    }
  }

  // Farbe-in-Farbe, pro Bild berechnet:
  // - Pille = Bildfarbe, leicht abgedunkelt, mit Wunsch-Deckkraft cfg.alpha
  // - Schrift = Farbe des Untergrunds, nur so weit Richtung Weiß/Schwarz geschoben,
  //   dass das ungünstigste Messfeld genau den Zielkontrast cfg.contrast erreicht
  // - reicht selbst reines Weiß/Schwarz nicht, wird die Deckkraft schrittweise erhöht
  function palette(cells) {
    const avg = [0, 1, 2].map((k) => cells.reduce((s, c) => s + c[k], 0) / cells.length);
    const W = [255, 255, 255], B = [0, 0, 0];
    const worstRaw = (t) => Math.min(...cells.map((c) => contrast(t, c)));
    const light = worstRaw(W) >= worstRaw(B);                                   // helle oder dunkle Schrift?
    const extreme = light ? W : B;
    // Pille immer gegenläufig zur Schrift tönen (sonst kostet mehr Deckkraft Kontrast).
    // Ausnahme: sehr heller Grund → Hauch dunkler, damit die Pille als Form sichtbar bleibt.
    const tint = light ? mix(avg, B, 0.55)
               : lum(avg) > 0.75 ? mix(avg, B, 0.1) : mix(avg, W, 0.55);
    const worst = (text, a) => Math.min(...cells.map((c) => contrast(text, mix(c, tint, a))));
    for (let a = cfg.alpha; a <= 0.9; a += 0.04) {
      const under = mix(avg, tint, a);
      if (worst(extreme, a) < cfg.contrast) continue;           // nicht erreichbar → Pille kräftiger
      let lo = 0, hi = 1;                                        // kleinste Verschiebung finden
      for (let i = 0; i < 14; i++) {
        const mid = (lo + hi) / 2;
        worst(mix(under, extreme, mid), a) >= cfg.contrast ? (hi = mid) : (lo = mid);
      }
      const text = mix(under, extreme, hi);
      return { text, tint, alpha: +a.toFixed(2), contrast: worst(text, a) };
    }
    return { text: extreme, tint, alpha: 0.9, contrast: worst(extreme, 0.9) };
  }

  // --- Label ---------------------------------------------------------------
  function basename(src) {
    try {
      const n = new URL(src, location.href).pathname.split('/').pop();
      return decodeURIComponent(n).replace(/_(?:\d+x\d*|x\d+)(?:@\dx)?(?=\.\w+$)/, '');
    } catch (e) { return ''; }
  }

  function place(img, label) {
    // Label nur zeigen, wenn das KI-Bild selbst gerendert wird (z. B. verstecktes Handy-/Desktop-Bild)
    const ir = img.getBoundingClientRect();
    label.hidden = ir.width <= 2 || ir.height <= 2 || getComputedStyle(img).display === 'none';
    if (label.hidden) return;
    const host = label.offsetParent || img.parentElement;
    const hr = host.getBoundingClientRect(), r = img.getBoundingClientRect();
    label.style.right = `${hr.right - r.right + 8}px`;
    let bottom = hr.bottom - r.bottom + 8;
    // Theme setzt Sale/Ausverkauft-Badges unten rechts (badge_position) → Label rückt darüber
    const badge = img.closest('.card-wrapper, .card')?.querySelector('.card__badge .badge');
    if (badge && badge.offsetParent) {
      const br = badge.getBoundingClientRect();
      if (br.right > r.right - 80 && br.bottom > r.bottom - 60) bottom = hr.bottom - br.top + 6;
    }
    label.style.bottom = `${bottom}px`;
    const lr = label.getBoundingClientRect();
    const box = { x: lr.left - r.left, y: lr.top - r.top, w: lr.width, h: lr.height };
    const cells = sample(img, box) || [[40, 40, 40]];
    const p = palette(cells);
    label.style.setProperty('--ki-text', rgb(p.text));
    label.style.setProperty('--ki-bg', rgb(p.tint, p.alpha));
    label.style.setProperty('--ki-bg-hover', rgb(p.tint, Math.min(0.9, p.alpha + 0.18)));
    label.dataset.alpha = p.alpha;
    label.dataset.contrast = p.contrast.toFixed(1);
  }

  function attach(img) {
    if (img.dataset.kiLabel) return;
    // Vorschaubilder der Produktgalerie (Thumbnails) bekommen kein Label, nur das große Bild
    if (img.closest('.thumbnail-list, .thumbnail, .slider--thumbnails, [id^="Slider-Thumbnails"]')) return;
    const kind = files[basename(img.currentSrc || img.src)];
    if (!kind) return;
    img.dataset.kiLabel = kind;
    // Über Link-/Text-Ebenen des Themes legen (Karten-Link, Banner-Text, Zoom-Button), damit Hover und Klick ankommen
    const host = img.closest('.card, .slideshow__slide, .banner, .product-media-container') || img.parentElement;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const a = document.createElement('a');
    a.className = 'ki-label';
    a.href = cfg.href;
    a.innerHTML = `<span class="ki-label__ai">AI</span><span class="ki-label__more">${cfg.short[kind]}</span>`;
    // Zweitbild einer Produktkarte (erscheint erst bei Hover) → Label ebenfalls nur bei Hover
    if (img.previousElementSibling?.tagName === 'IMG' && img.closest('.media--hover-effect')) a.classList.add('ki-label--hover-only');
    a.title = `${cfg.text[kind]} · mehr erfahren`;
    a.setAttribute('aria-label', `${cfg.text[kind]}, mehr zur KI-Nutzung`);
    host.appendChild(a);
    const update = () => place(img, a);
    img.complete ? update() : img.addEventListener('load', update, { once: true });
    new ResizeObserver(update).observe(img);
  }

  const scan = (root) => root.querySelectorAll?.('img').forEach(attach);
  scan(document);
  new MutationObserver((ms) => ms.forEach((m) => m.addedNodes.forEach((n) => {
    if (n.tagName === 'IMG') attach(n); else scan(n);
  }))).observe(document.documentElement, { childList: true, subtree: true });
})();
